const MARKER = '__alefAppBackV1';

// A single same-URL base/guard pair handles state-based dashboards without
// pushing an entry for every render, modal or menu click. Preserve Router state.
export function installAppBackHistory({ onBack, onUnavailable, owner: ownerOverride, win = window }) {
  const existing = win.history.state?.[MARKER];
  const owner = ownerOverride || existing?.owner || `back-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  let exiting = false;
  let exitDepth = 0;
  let exitTimer;
  const previousScrollRestoration = win.history.scrollRestoration;
  const state = () => ({ ...(win.history.state || {}) });
  const restore = () => {
    const current = state();
    const marker = current[MARKER];
    if (marker?.owner === owner && marker.stage === 'guard') return;
    const base = marker?.owner === owner ? marker : { owner, canLeave: win.history.length > 1 };
    win.history.replaceState({ ...current, [MARKER]: { ...base, stage: 'base' } }, '', win.location.href);
    win.history.pushState({ ...current, [MARKER]: { ...base, stage: 'guard' } }, '', win.location.href);
  };
  const unavailable = () => {
    exiting = false;
    win.clearTimeout(exitTimer);
    if (exitDepth > 0) win.history.go(exitDepth);
    else if (win.history.state?.[MARKER]?.stage === 'base') win.history.forward();
    else restore();
    onUnavailable();
  };
  const onPop = (event) => {
    const marker = event.state?.[MARKER];
    if (exiting) {
      if (marker?.owner === owner) {
        exitDepth += 1;
        event.stopImmediatePropagation();
        if (marker.stage === 'base' && !marker.canLeave) unavailable();
        else win.history.back();
      } else {
        exiting = false;
        win.clearTimeout(exitTimer);
      }
      return;
    }
    if (marker?.owner !== owner) return;
    event.stopImmediatePropagation();
    if (marker.stage !== 'base') return;
    // Reuse the existing forward entry. Pushing another entry here can make
    // Chromium skip the document on the next hardware Back without a new tap.
    win.history.forward();
    onBack();
  };
  restore();
  if (previousScrollRestoration !== undefined) win.history.scrollRestoration = 'manual';
  win.addEventListener('popstate', onPop, true);
  return {
    owner,
    exit() {
      if (exiting) return;
      exiting = true;
      exitDepth = 0;
      win.history.back();
      // A browser with no previous page cannot close a normal tab by script.
      exitTimer = win.setTimeout(() => {
        if (exiting && win.document.visibilityState !== 'hidden') unavailable();
      }, 1500);
    },
    destroy() {
      win.clearTimeout(exitTimer);
      win.removeEventListener('popstate', onPop, true);
      if (previousScrollRestoration !== undefined) win.history.scrollRestoration = previousScrollRestoration;
    },
  };
}
